<template>
  <CustomDrawer
    v-model:modelValue="internalDrawerVisible"
    direction="right"
    width="600px"
    title="Phone Number Details"
  >
    <div :id="`${source}_update_phone_number`">
      <SkeletonLoader v-if="loading" :page="loaderPages.PHONE_NUMBER_DETAILS" />

      <div
        v-else
        :id="`${source}_update_phone_number_section`"
        class="flex flex-col h-full justify-between"
      >
        <div class="flex flex-col gap-10 text-white font-medium">
          <PhoneNumberSection
            v-if="formData.generatedNumberDetails.number"
            :internal-form-data="formData"
            :should-show-phone-number="true"
            :enable-regenerate="false"
          />

          <div class="flex flex-col gap-6">
            <div id="name" class="flex flex-col gap-1">
              <span class="secondary_color text-[13px]">Name</span>
              <a-input
                v-model:value="formData.name"
                class="input_medium"
                placeholder="Enter name"
              />
            </div>

            <AssignmentSection
              :internal-form-data="formData"
              @reset-form="resetFormState"
            />

            <OutboundCallerIDSection
              :source="source"
              :form-data="formData"
              :provider="provider"
            />

            <ForwardCallsSection
              v-if="isCallForwardingEnabled"
              :source="source"
              :form-data="formData"
            />
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex flex-row gap-2 justify-end">
        <a-button
          class="primary_medium"
          @click="toUpdatePhoneNumberDetails"
          :loading="updating"
        >
          {{ updating ? "Saving.." : "Save Changes" }}
        </a-button>
      </div>
    </template>
  </CustomDrawer>
</template>

<script setup>
import { ref, watch, reactive, computed } from "vue";
import { message } from "ant-design-vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { assignCallTypes } from "@/src/constants/callConstants.js";
import { telephonyProviders } from "@/src/constants/telephonyConstants.js";
import eventBus from "@/src/utils/eventBus.js";
import CallService from "@/src/api/CallService.js";
import CustomDrawer from "@/src/components/CustomDrawer.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import AssignmentSection from "@/src/components/telephony_components/phone_number/sections/AssignmentSection.vue";
import PhoneNumberSection from "@/src/components/telephony_components/phone_number/sections/PhoneNumberSection.vue";
import ForwardCallsSection from "@/src/components/telephony_components/phone_number/sections/ForwardCallsSection.vue";
import OutboundCallerIDSection from "@/src/components/telephony_components/phone_number/sections/OutboundCallerIDSection.vue";

const props = defineProps({
  source: { type: String, required: true },
  data: { type: Object, required: true },
  drawerVisible: { type: Boolean, required: true },
});

const emit = defineEmits(["update:modelValue"]);

const loading = ref(false);
const updating = ref(false);
const provider = ref(telephonyProviders.twilio);

const internalDrawerVisible = ref(props.drawerVisible);

const formData = reactive({
  assignedTo: "user",
  userId: null,
  teamId: null,
  name: "",
  outboundCallerID: 0,
  forwardCallsEnabled: false,
  forwardToNumber: "",
  generatedNumberDetails: {
    number: "",
    friendly_name: "",
    area: "",
  },
});

const isCallForwardingEnabled = computed(() => {
  return (
    !!localStorage.getItem("call_forwarding") &&
    provider.value === telephonyProviders.twilio
  );
});

const resetFormState = async () => {
  try {
    if (formData.assignedTo === assignCallTypes.USER) {
      formData.teamId = null;
    } else if (formData.assignedTo === assignCallTypes.TEAM) {
      formData.userId = null;
    }
  } catch (error) {
    console.error("An error occurred while resetting form state: ", error);
  }
};

const toGetPhoneNumberDetails = async (id) => {
  try {
    loading.value = true;

    const response = await CallService.getPhoneNumberDetails(id);
    if (response?.success) {
      const data = response?.data;

      formData.name = data?.phone_number_name || "";
      formData.userId = data?.user_id || null;
      formData.teamId = data?.team_id || null;
      formData.outboundCallerID = data?.caller_id_id || 0;
      formData.forwardCallsEnabled = data?.is_forwarding_enabled || false;
      formData.forwardToNumber = data?.forwarding_number || "";

      formData.generatedNumberDetails.number = data?.phone_number || "";
      formData.generatedNumberDetails.friendly_name = data?.friendly_name || "";
      formData.generatedNumberDetails.area = [data?.iso_country]
        .filter((item) => item)
        .join(", ");

      if (data?.type) {
        formData.assignedTo = data.type.toLowerCase();
      } else {
        formData.assignedTo =
          data?.user_id && data.user_id !== 0 ? "user" : "team";
      }

      if (data?.vendor) {
        provider.value = data.vendor;
      }
    }
  } catch (error) {
    console.error(
      "An error occurred while getting phone number details: ",
      error,
    );
  } finally {
    loading.value = false;
  }
};

const toUpdatePhoneNumberDetails = async () => {
  try {
    updating.value = true;

    const payload = {
      id: props.data.id,
      name: formData.name,
      user_id: formData.userId,
      team_id: formData.teamId,
      callerid_id:
        formData.outboundCallerID === 0 ? null : formData.outboundCallerID,
      is_enabled: formData.forwardCallsEnabled,
      forwarding_number: formData.forwardToNumber,
    };

    const response = await CallService.updatePhoneNumberDetails(payload);
    if (response?.success) {
      internalDrawerVisible.value = false;
      message.success("Phone number details updated successfully!");
      eventBus.emit("refresh-phone-numbers");
    }
  } catch (error) {
    console.error("An error occurred while updating phone number: ", error);
    message.error(
      error?.response?.data?.error || "Failed to update phone number",
    );
  } finally {
    updating.value = false;
  }
};

watch(
  () => props.data,
  (newData) => {
    if (newData) {
      toGetPhoneNumberDetails(newData.id);
    }
  },
  { immediate: true },
);

watch(
  () => props.drawerVisible,
  (newVal) => {
    internalDrawerVisible.value = newVal;

    if (newVal && props.data?.id) {
      toGetPhoneNumberDetails(props.data.id);
    }
  },
);

watch(
  () => internalDrawerVisible.value,
  (newVal) => {
    if (!newVal) {
      emit("update:modelValue", false);
    }
  },
);
</script>
